import { FloatingPanel } from '@mews/b2b-ui';
import { Button, Stack } from '@optimus-web/core';
import {
    DateTimeService,
    DateTimeUtils,
    FormCheckboxField,
    FormInputField,
    FormSwitchField,
    LocalizedText,
    lt,
    showSuccessAlert,
    useAppClockService,
    useTimezone,
    useFormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DateTimeField, EnumSelectField } from '@mews-commander/form';
import { ExportScheduleFrequencyEnum, ExportScheduleFrequency } from '@mews-commander/data-access';
import { OptimusProvider } from '@mews-commander/ui';
import { ScheduleValueKey, useUpdateExportScheduleMutation } from '@mews-commander/reporting-framework';

interface EditScheduleDialogProps {
    exportName: string;
    frequency: ExportScheduleFrequencyEnum;
    isEnabled: boolean;
    notifyCreatorAboutExport: boolean;
    onClose: () => void;
    scheduleExportId: string;
    startUtc: string;
}

export const EditScheduleDialog = ({
    exportName,
    frequency,
    isEnabled,
    notifyCreatorAboutExport,
    onClose,
    scheduleExportId,
    startUtc,
}: EditScheduleDialogProps) => {
    const timezone = useTimezone();
    const nowUtc = useAppClockService().getAppNowUtc();
    const minDate = DateTimeUtils.utcToStructuredDate(nowUtc.value, timezone);
    const defaultDateUtc = DateTimeService.toUtc(DateTimeService.fromUtcString(startUtc, timezone));
    const defaultFrequency = Object.keys(ExportScheduleFrequencyEnum).find(
        (key) => ExportScheduleFrequencyEnum[key as keyof typeof ExportScheduleFrequencyEnum] === frequency
    );

    const updateMutation = useUpdateExportScheduleMutation({
        onSuccess: () => {
            showSuccessAlert({
                description: lt('ExportScheduleSuccessfullyEdited'),
            });
            onClose();
        },
    });

    const handleSubmit = (values: {
        exportName: string;
        frequency: ExportScheduleFrequency;
        startUtc: string;
        isEnabled: boolean;
        notifyCreatorAboutExport: boolean;
    }) => {
        updateMutation.mutate({
            exportName: values.exportName,
            frequency: values.frequency,
            isEnabled: values.isEnabled,
            notifyCreatorAboutExport: values.notifyCreatorAboutExport,
            startUtc: values.startUtc,
            scheduleExportId,
        });
    };

    const { renderFormContent, submit } = useFormControls({
        defaultValues: {
            exportName,
            frequency: defaultFrequency,
            isEnabled,
            startUtc: defaultDateUtc.value,
            notifyCreatorAboutExport,
        },
        id: 'editSchedule',
        onSubmit: handleSubmit,
        resolver: undefined,
    });

    return renderFormContent(
        <OptimusProvider density="spacious">
            <FloatingPanel
                active
                footer={{
                    primary: (
                        <Button onClick={submit} type="submit" variant="primary">
                            <LocalizedText textKey={TextKey.Save} />
                        </Button>
                    ),
                    secondary: (
                        <Button onClick={onClose} type="button" variant="tertiary">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                }}
                onClose={onClose}
                title={<LocalizedText textKey={TextKey.EditScheduleConfiguration} />}
            >
                <Stack spacing="300" vertical>
                    <FormInputField label={<LocalizedText textKey={TextKey.Name} />} name="exportName" required />
                    <EnumSelectField
                        name={ScheduleValueKey.frequency}
                        enumClass={ExportScheduleFrequency}
                        enumPrefix="ExportScheduleFrequency"
                        label={<LocalizedText textKey={TextKey.Frequency} />}
                        omitHint
                        placeholder={<LocalizedText textKey={TextKey.Frequency} />}
                        showEmptyValue={false}
                        required
                    />
                    <DateTimeField dateProps={{ minDate }} label={lt('Start')} required name="startUtc" />
                    <Stack spacing="200" vertical>
                        <FormCheckboxField name={ScheduleValueKey.notifyCreatorAboutExport}>
                            <LocalizedText textKey={TextKey.NotifyCreatorAboutExport} />
                        </FormCheckboxField>
                        <FormSwitchField
                            label={<LocalizedText textKey={TextKey.ActivateExportSchedule} />}
                            name={ScheduleValueKey.isActive}
                        />
                    </Stack>
                </Stack>
            </FloatingPanel>
        </OptimusProvider>
    );
};
